import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Filter, X, Search } from 'lucide-react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { useLanguage } from '../context/LanguageContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shop = () => {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('default');
  const [showFilters, setShowFilters] = useState(false);
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const { t } = useLanguage();

  const categories = [
    { id: '', name: t.shop.allProducts },
    { id: 'dewalt-batteries', name: t.nav.powerTools },
    { id: 'milwaukee-batteries', name: t.nav.handTools },
    { id: 'makita-batteries', name: t.nav.accessories },
    { id: 'tools-accessories', name: t.nav.toolsAccessories }
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        let url = category 
          ? `${API}/products?category=${category}`
          : `${API}/products`;
        
        // Add search parameter
        if (searchQuery) {
          url += `${url.includes('?') ? '&' : '?'}search=${encodeURIComponent(searchQuery)}`;
        }
        
        const response = await axios.get(url);
        let data = response.data;

        if (sortBy === 'price-asc') {
          data = [...data].sort((a, b) => a.price - b.price);
        } else if (sortBy === 'price-desc') {
          data = [...data].sort((a, b) => b.price - a.price);
        } else if (sortBy === 'name') {
          data = [...data].sort((a, b) => a.name.localeCompare(b.name));
        }

        setProducts(data);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [category, sortBy, searchQuery]);

  const handleLocalSearch = (e) => {
    e.preventDefault();
    if (localSearch.trim()) {
      setSearchParams({ search: localSearch.trim() });
    } else {
      setSearchParams({});
    }
  };

  const clearSearch = () => {
    setLocalSearch('');
    setSearchParams({});
  };

  const currentCategory = categories.find(c => c.id === (category || ''));

  return (
    <div className="min-h-screen" data-testid="shop-page">
      <div className="bg-[#111111] border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-yellow-500 mb-2 block">
            {t.shop.title}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase leading-none text-white" data-testid="shop-title">
            {currentCategory?.name || t.shop.allProducts}
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <div className="mb-6">
          <form onSubmit={handleLocalSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type="text"
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                placeholder={t.shop.searchPlaceholder || "Szukaj produktów..."}
                className="w-full bg-[#111111] border border-zinc-700 text-white pl-10 pr-4 py-3 focus:border-yellow-400 focus:outline-none"
                data-testid="shop-search-input"
              />
            </div>
            <button 
              type="submit"
              className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-6 py-3 transition-colors"
            >
              {t.shop.search || "Szukaj"}
            </button>
            {searchQuery && (
              <button 
                type="button"
                onClick={clearSearch}
                className="bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-3 transition-colors"
              >
                <X size={20} />
              </button>
            )}
          </form>
          {searchQuery && (
            <p className="text-zinc-400 text-sm mt-2">
              Wyniki wyszukiwania dla: <span className="text-yellow-400">"{searchQuery}"</span> ({products.length} {products.length === 1 ? 'produkt' : 'produktów'})
            </p>
          )}
        </div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pb-8 border-b border-zinc-800">
          <div className="hidden md:flex items-center gap-4">
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={cat.id ? `/shop/${cat.id}` : '/shop'}
                className={`font-mono text-sm tracking-widest uppercase transition-colors ${
                  (category || '') === cat.id 
                    ? 'text-yellow-400' 
                    : 'text-zinc-400 hover:text-white'
                }`}
                data-testid={`filter-category-${cat.id || 'all'}`}
              >
                {cat.name}
              </a>
            ))}
          </div>

          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden flex items-center gap-2 font-mono text-sm tracking-widest uppercase text-zinc-400"
            data-testid="mobile-filter-toggle"
          >
            <Filter size={16} /> {t.shop.filters}
          </button>

          <div className="flex items-center gap-4">
            <span className="font-mono text-xs tracking-widest uppercase text-zinc-500">{t.shop.sortBy}</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px] bg-[#111111] border-zinc-700 text-white rounded-none focus:ring-yellow-400" data-testid="sort-select">
                <SelectValue placeholder={t.shop.default} />
              </SelectTrigger>
              <SelectContent className="bg-[#111111] border-zinc-700 rounded-none">
                <SelectItem value="default" className="text-white hover:bg-[#1A1A1A]">{t.shop.default}</SelectItem>
                <SelectItem value="price-asc" className="text-white hover:bg-[#1A1A1A]">{t.shop.priceLowHigh}</SelectItem>
                <SelectItem value="price-desc" className="text-white hover:bg-[#1A1A1A]">{t.shop.priceHighLow}</SelectItem>
                <SelectItem value="name" className="text-white hover:bg-[#1A1A1A]">{t.shop.name}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {showFilters && (
          <div className="md:hidden mb-8 p-4 bg-[#111111] border border-zinc-800">
            <div className="flex items-center justify-between mb-4">
              <span className="font-mono text-xs tracking-widest uppercase text-zinc-400">{t.home.categories}</span>
              <button onClick={() => setShowFilters(false)}>
                <X size={20} className="text-zinc-400" />
              </button>
            </div>
            <div className="flex flex-col gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.id ? `/shop/${cat.id}` : '/shop'}
                  className={`py-2 font-mono text-sm tracking-widest uppercase transition-colors ${
                    (category || '') === cat.id 
                      ? 'text-yellow-400' 
                      : 'text-zinc-400'
                  }`}
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-0">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="border border-zinc-800 p-4 sm:p-6 animate-pulse">
                <div className="aspect-square bg-zinc-800 mb-4" />
                <div className="h-4 bg-zinc-800 mb-2 w-1/3" />
                <div className="h-6 bg-zinc-800 mb-4" />
                <div className="h-6 bg-zinc-800 w-1/2" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-zinc-400 text-lg">{t.shop.noProducts}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-0" data-testid="products-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}

        <div className="mt-8 pt-8 border-t border-zinc-800">
          <p className="font-mono text-sm text-zinc-500">
            {t.shop.showing} {products.length} {t.shop.products}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Shop;
